import AngleLeft from "@icons/angle-left.svg?react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { z } from "zod";

import { traderSchema } from "@/api/types/digitalnisklady.cz/types";
import {
  canSignCheckFn,
  useGuard,
} from "@/common/acl/action-guard/action-guard";
import { BindingOfferOrderAllTradersDialog } from "@/common/components/binding-offer-dialog/binding-offer-order-all-traders-dialog";
import { BindingOfferOrderDialog } from "@/common/components/binding-offer-dialog/binding-offer-order-dialog";
import { Button } from "@/common/components/button/button";
import { Metadata } from "@/common/components/metadata/metadata";
import { trackTradingOrderClick } from "@/common/components/mixpanel/mixpanel-tracking";
import { Fields, OrderForm } from "@/common/components/order-form/order-form";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { useCurrency } from "@/common/hooks/use-currency";
import { useFormConfirmation } from "@/common/hooks/use-form-confirmation";
import { entitySchema } from "@/common/schema/entity";
import { currencyFormatter } from "@/common/services/formatter";
import { invariant } from "@/common/utils/invariant";

import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { handleFormError } from "../shared/api/error-handler";
import { path as failedPath } from "../shared/routes/failed/failed";
import { path as successPath } from "../shared/routes/success/success";
import { futureQuartersQuery, useAddOrderMutation } from "./trades.api";

const path = "order";
const TradesDetailOrder = () => {
  const { _ } = useLingui();
  const { offerId } = useParams();
  const [searchParams] = useSearchParams();
  invariant(offerId);
  const [, currency] = useCurrency();
  const context = useTradesDetailOrder();
  const navigate = useNavigate();
  const addOrder = useAddOrderMutation();
  const { confirm, dialog, formData, rejectRef, resolveRef } =
    useFormConfirmation<Fields>();
  // form gives quarter ID only; need name for dialog — deduped with OrderForm.QuarterField
  const quarters = useSuspenseQuery(futureQuartersQuery());

  const handleSubmit = (data: Fields) => {
    trackTradingOrderClick({
      crop: context.crop.name,
      currency,
      price: data.pricePerTon,
      quarter: context.quarter.name,
    });
    addOrder.mutate(
      {
        amount: data.tons,

        // @ts-expect-error discrepancy between CF API and IDSign API
        currency,
        customTransport: data.customTransport,
        lowPrice: data.lowPricePerTon,
        note: data.note,
        offerId,
        ...(data.trader === "ALL" ? {} : { onlyTrader: data.trader }),
        price: data.pricePerTon,
        quarterId: data.quarter,
      },
      {
        onError: async (e) => {
          const errors = handleFormError(e);
          await navigate(
            "../../" + failedPath + "?" + searchParams.toString(),
            {
              state: { errors },
            },
          );
        },
        onSuccess: async (mutation) => {
          await navigate(
            "../../" + successPath + "/order/" + mutation.addOrder?.id,
          );
        },
      },
    );
  };
  const guardedSubmit = useGuard({
    action: handleSubmit,
    checkFn: canSignCheckFn,
  });

  const handleFormSubmit = (data: Fields) => {
    confirm(
      <Trans>Přejete si nastavit příkaz k prodeji?</Trans>,
      data,
      guardedSubmit,
    );
  };

  const selectedTrader =
    formData?.trader && formData.trader !== "ALL"
      ? context.traders.find((t) => t.id === formData.trader)
      : undefined;
  const selectedQuarterName =
    quarters.data.find((q) => q.id === formData?.quarter)?.name ??
    context.quarter.name;

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <SearchParamsLink replace to="..">
            <AngleLeft />
          </SearchParamsLink>
        }
      >
        <Metadata title={_(msg`Nastavit příkaz k prodeji`)} />
        <FullscreenModalLayout.Title>
          <Trans>Nastavit příkaz k prodeji</Trans>
        </FullscreenModalLayout.Title>
        <PropertiesList
          properties={[
            {
              label: <Trans>Komodita</Trans>,
              value: context.crop.name,
            },
            {
              label: <Trans>Farma</Trans>,
              value: context.company.name,
            },
            {
              label: <Trans>Sklad</Trans>,
              value: context.storage.name,
            },
          ]}
        />
        <Spacer className="h-6" />
        <OrderForm
          initialValues={{
            customTransport: context.hasCustomTransport,
            customTransportNote: context.customTransportNote,
            note: context.note,
            quarter: context.quarter.id,
            tons: context.tons,
            trader: context.trader.id,
          }}
          onSubmit={handleFormSubmit}
        >
          <OrderForm.QuarterField />
          <OrderForm.LowPriceField currency={currency} />
          <OrderForm.PriceField currency={currency} />
          <OrderForm.TraderField>
            <option value="ALL">
              <Trans>Všichni obchodníci</Trans>
            </option>
            {context.traders.map((trader) => (
              <option key={trader.id} value={trader.id}>
                {trader.name}
              </option>
            ))}
          </OrderForm.TraderField>
          <OrderForm.AmountField />
          <OrderForm.CustomTransportField />
          <OrderForm.NoteField />
        </OrderForm>
        <Button.Container>
          <Button
            disabled={addOrder.status === "pending"}
            form={OrderForm.formId}
            type="submit"
            variant="primary"
          >
            <Trans>Nastavit příkaz</Trans>
          </Button>
        </Button.Container>
      </FullscreenModalLayout>
      {dialog && formData ? (
        formData.trader === "ALL" ? (
          <BindingOfferOrderAllTradersDialog
            amount={formData.tons + " t"}
            cropName={context.crop.name}
            isPending={addOrder.status === "pending"}
            onCancel={() => rejectRef.current?.()}
            onConfirm={(e) => resolveRef.current?.(e)}
            postalCodeLabel={context.postalCode}
            quarter={selectedQuarterName}
            unitPrice={currencyFormatter(currency).format(formData.pricePerTon)}
          />
        ) : (
          <BindingOfferOrderDialog
            amount={formData.tons + " t"}
            cropName={context.crop.name}
            isPending={addOrder.status === "pending"}
            onCancel={() => rejectRef.current?.()}
            onConfirm={(e) => resolveRef.current?.(e)}
            postalCodeLabel={context.postalCode}
            quarter={selectedQuarterName}
            timeFromTrading={selectedTrader?.timeFromTrading ?? null}
            timeToTrading={selectedTrader?.timeToTrading ?? null}
            tocUrl={selectedTrader?.tocUrl ?? null}
            traderName={selectedTrader?.name ?? ""}
            unitPrice={currencyFormatter(currency).format(formData.pricePerTon)}
          />
        )
      ) : null}
    </>
  );
};

const contextSchema = z.object({
  company: entitySchema,
  crop: entitySchema,
  customTransportNote: z.string().optional(),
  hasCustomTransport: z.boolean().optional(),
  note: z.string().optional(),
  offer: z.object({
    amount: z.number(),
  }),
  postalCode: z.string(),
  quarter: entitySchema,
  storage: entitySchema,
  tons: z.number().optional(),
  trader: traderSchema,
  traders: z.array(traderSchema),
});

const useTradesDetailOrder = () => {
  const context = useOutletContext();
  return contextSchema.parse(context);
};

export { path, TradesDetailOrder };
